import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';

import { APP_NAME } from '../../constants/app-branding.constants';

type AppLoaderSize = 'small' | 'medium' | 'large';

/**
 * Brand loader — identity + activity, never both on one element.
 *
 *   • Full-screen: vertical Harvey-clean lockup (inline mark SVG +
 *     wordmark) with a hairline attestation track and an optional
 *     slow-hint label.
 *   • Inline: optional `logoSrc` / title / description + progress
 *     shimmer for embedded loading states.
 *
 * Full-screen sits on charcoal `--app-bg` so pre-boot → Angular → login
 * never flashes a different plane. The composition mirrors the
 * pre-boot markup in `index.html` so mount is seamless.
 *
 * Copy notes:
 *   - `title` / `description` are inputs — call-sites pass translated
 *     strings when they care about locale.
 *   - `slowHintLabel` and the SR fallback ("Loading") are hardcoded
 *     English defaults. Kept as literals (rather than wiring
 *     `TranslocoService`) because this component renders in the boot
 *     path before app services are fully bootstrapped for the pre-
 *     boot handoff parity — matching the pre-boot HTML in
 *     `index.html` which also cannot use Transloco. If a locale-
 *     sensitive slow-hint is ever needed, prefer promoting it to an
 *     input over pulling Transloco into this component.
 */
@Component({
  selector: 'app-loader',
  templateUrl: './app-loader.html',
  styleUrl: './app-loader.css',
  imports: [NgOptimizedImage],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppLoaderComponent {
  readonly title = input<string | null>(null);
  readonly description = input<string | null>(null);
  readonly size = input<AppLoaderSize>('medium');
  readonly minHeight = input<string | null>(null);
  readonly fullScreen = input<boolean>(false);
  /** Optional brand-mark image for inline embeds. Ignored when `fullScreen`. */
  readonly logoSrc = input<string | null>(null);
  readonly slowHint = input<boolean>(false);

  protected readonly brandName = APP_NAME;
  /** Bottom-anchored micro-copy on the splash. Hardcoded English —
   *  see class comment for the design rationale. */
  protected readonly slowHintLabel = 'Preparing your workspace';
}
